// The on-demand course's home, /cashflow (Property Cashflow For Life; no board, so it's PCFL v3's Home,
// reference/pcfl-v3/source/app.jsx, in the hub's frame and the course's own look). The welcome, then:
//
//   news    Adam's announcement for this course, while he has 1 up
//   where   where they're up to: the 5 Ms pentagon, the module they're on (or Start here, or every
//           module done, with the certificate once it's issued) and their homework line (fixed: it
//           moves, never hides)
//   next    Up next: Start here until it's done, and the recording to carry on with or watch next
//   hw      the homework of the module they're on, ticks saved as they go (which module is fixed
//           when the page opens, so the last tick doesn't take the card away)
//
// The sections follow the course's published home layout, else this default (layout.js's rules: on
// a laptop the where card and the homework on the left, Up next beside them; on a phone and between,
// 1 column in this order). The frame, the payment banner and the welcome checklist are the product
// route's. src/products/ondemand/model.js works out what it says. While staff view as a member,
// nothing here writes.
import { useRef } from 'react'
import { useMember } from '../../app/member.jsx'
import { useNow } from '../../lib/clock.js'
import { Link } from '../../lib/router.jsx'
import { DOORS } from '../../config/doors.js'
import { useProductData } from '../../product/ProductData.jsx'
import { useLayout, LayoutSections } from '../../product/Layout.jsx'
import { ProductAnnouncement } from '../../product/ProductAnnouncement.jsx'
import { productAnnouncement } from '../../product/model.js'
import { COPY } from '../../products/ondemand/copy.js'
import { Pentagon } from '../../products/ondemand/Pentagon.jsx'
import { Homework, Thumb } from '../../products/ondemand/parts.jsx'
import { courseModel, startCard, watchCard } from '../../products/ondemand/model.js'
import { ON_DEMAND_HOME } from '../../products/ondemand/layout.js'
import { thumbItem } from '../../product/recordingThumbs.js'
import { useRecordingThumbs } from '../../product/RecordingThumb.jsx'
import '../../styles/ondemand.css'
import { realFirstName } from '../../lib/names.js'

const COLUMNS = 'minmax(0, 1.45fr) minmax(0, 1fr)'

export default function OnDemandHome({ product }) {
  const { data } = useProductData()
  const { me, lib } = useMember()
  const nowMs = useNow()
  const sections = useLayout(product, 'home', ON_DEMAND_HOME)
  const keep = useRef(null)
  if (!sections) return <div className="od od-home" aria-busy="true" />

  const model = courseModel(data, product)
  if (keep.current === null) keep.current = model.openHw?.id || false
  const hwModule = keep.current ? model.modules.find(m => m.id === keep.current) || null : null
  const start = startCard(model, product)
  const watch = watchCard(model, product, data)
  const announcement = productAnnouncement(lib.data, product.id, nowMs)
  // "Welcome back, Sam", or "Welcome back" when the first name is the database's "there" or none (lib/names.js)
  const name = realFirstName(me.person?.first_name)
  const tagline = DOORS[product.slug]?.tagline || null

  const parts = {
    news: { place: 'full', when: Boolean(announcement), render: () => <ProductAnnouncement announcement={announcement} /> },
    where: { place: 'main', columns: COLUMNS, render: () => <Where model={model} product={product} /> },
    next: { place: 'side', columns: COLUMNS, when: Boolean(start || watch), render: () => <UpNext start={start} watch={watch} model={model} /> },
    hw: {
      place: 'main', columns: COLUMNS, when: Boolean(hwModule),
      render: () => <section className="od-card od-pad od-hw-card"><Homework module={hwModule} /></section>,
    },
  }

  return (
    <div className="od od-home">
      <header className="od-head">
        <h1 className="od-h1">{COPY.welcome(name)}</h1>
        {tagline && <p className="od-lede">{tagline}</p>}
      </header>
      <LayoutSections sections={sections} parts={parts} className="od-layout" />
    </div>
  )
}

function Where({ model, product }) {
  const { where } = model
  return (
    <section className="od-card od-pad od-where" aria-labelledby="od-where-h">
      {model.pentagon && (
        <div className="od-where-pent">
          <Pentagon states={model.states} labels names={model.numbered.map(m => m.title)} title={COPY.pentagonTitle} />
          <p className="od-where-key">{COPY.pentagonKey}</p>
        </div>
      )}
      <div className="od-where-text">
        <div className="od-kicker">{COPY.whereKicker}</div>
        <h2 className="od-where-h" id="od-where-h">{where.headline}</h2>
        {where.line && <p className="od-where-line">{where.line}</p>}
        {!model.pentagon && model.numbered.length > 0 && (
          <p className="od-small">{COPY.modulesDone(model.numbered.filter(m => model.finished(m)).length, model.numbered.length)}</p>
        )}
        <div className={`od-status od-status--${where.status.tone}`}>{where.status.text}</div>
        {model.certificate && <Link className="od-btn od-btn--primary od-where-btn" to={`${product.path}/certificate`}>{COPY.certificate}</Link>}
      </div>
    </section>
  )
}

function UpNext({ start, watch, model }) {
  const index = watch ? model.numbered.findIndex(m => m.id === watch.rec.module_id) : -1
  // the recording's own thumbnail where it has 1 (decision 47, 12 Oct)
  const thumbs = useRecordingThumbs(watch ? [{ item: thumbItem(watch.rec, watch.rec.ready) }] : [])
  return (
    <section className="od-next" aria-labelledby="od-next-h">
      <h2 className="od-section-h" id="od-next-h">{COPY.upNext}</h2>
      <div className="od-next-cards">
        {start && (
          <div className="od-card od-wk">
            <div className="od-kicker">{start.kicker}</div>
            <h3 className="od-wk-h">{start.title}</h3>
            <p className="od-wk-line">{start.line}</p>
            <div className="od-wk-act"><Link className="od-btn od-btn--primary od-btn--sm" to={start.button.href}>{start.button.label}</Link></div>
          </div>
        )}
        {watch && (
          <div className="od-card od-wk">
            <div className="od-kicker">{watch.kicker}</div>
            <div className="od-wk-rec">
              <Thumb row={watch.rec} index={index} state={index >= 0 ? model.states[index] : 'todo'} small thumbs={thumbs} pentagon={model.pentagon} />
              <div className="od-wk-rec-text">
                <h3 className="od-wk-h">{watch.title}</h3>
                <p className="od-wk-line">{watch.line}</p>
              </div>
            </div>
            {watch.pct != null && <div className="od-bar" aria-hidden="true"><span style={{ width: `${watch.pct}%` }} /></div>}
            <div className="od-wk-act"><Link className="od-btn od-btn--primary od-btn--sm" to={watch.button.href}>{watch.button.label}</Link></div>
          </div>
        )}
      </div>
    </section>
  )
}
